import { type Component } from 'vue';

type RouteCompiled = {
    definition: RouteDefinition;
    path: RoutePathCompiled;
};

export type RouteDefinition = {
    path: string;
    component: Component;
    title: string;
    onEnter?: () => void;
    onLeave?: () => void;
};

export type RouteParams = Record<string, string>;

type RoutePathCompiled = {
    regex: RegExp;
    keys: string[];
};

export type RouteMatch = (RouteDefinition & { params: RouteParams }) | null;

export const RoutePathsApp = {
    ERROR: 'error',
    LICENCE_ALLOCATION: 'licence-allocation',
    MY_ACCOUNT: 'my-account',
    MY_ACCOUNT_VERIFY_EMAIL: 'my-account/verify-email/:token',
    PROJECT: 'project',
    PROJECT_KEY_DATES: 'project/key-dates',
    PROJECT_KEY_DATES_FOR_PROJECT: 'project/:projectIdentifier/key-dates',
    PROJECT_WELCOME: 'project/welcome',
    REPORT_PERIOD_END: 'report/period-end',
    REPORT_PERIOD_END_PROJECT: 'report/period-end/project/:projectIdentifier',
    SCHEDULE: 'schedule'
} as const;

export const RoutePathsLogin = {
    ACCOUNT_ACTIVATION_FAILED: 'account-activation-failed',
    ACCOUNT_ACTIVATION_REQUIRED: 'account-activation-required',
    ACCOUNT_NOT_FOUND: 'account-not-found',
    FORGOTTEN_PASSWORD: 'forgotten-password',
    LOGIN: 'login',
    PASSWORD_RESET_LINK_EXPIRED: 'password-reset/link-expired',
    PASSWORD_RESET_TOKEN: 'password-reset/token/:token',
    SIGN_UP: 'sign-up',
    SINGLE_SIGN_ON: 'single-sign-on',
    SINGLE_SIGN_ON_NO_ACCOUNT: 'single-sign-on/no-account',
    TWO_FACTOR_RESET_ERROR: 'two-factor-reset/error',
    TWO_FACTOR_RESET_SUCCESS: 'two-factor-reset/success'
} as const;

export class Router {
    declare routes: RouteDefinition[];
    declare compiledRoutes: RouteCompiled[];

    constructor(routes: RouteDefinition[]) {
        this.updateRoutes(routes);
    }

    public updateRoutes(routes: RouteDefinition[]): void {
        this.routes = routes;

        this.compiledRoutes = this.routes.map((r: RouteDefinition) => ({
            definition: r,
            path: PathCompiler.compile(r.path)
        }));
    }

    public matchRoute(path: string): RouteMatch {
        for (const route of this.compiledRoutes) {
            const m = path.match(route.path.regex);

            if (!m) continue;

            const params: RouteParams = {};

            for (let i = 1; i < m.length; i++) {
                const key = route.path.keys[i - 1];
                params[key] = decodeURIComponent(m[i]);
            }

            return { ...route.definition, params };
        }

        return null;
    }
}

export class PathChecker {
    public static isMatch(path: string, paths: { [key: string]: string }): boolean {
        return Object.values(paths).some((p) => PathCompiler.compile(p).regex.test(path));
    }
}

class PathCompiler {
    public static compile(path: string): RoutePathCompiled {
        const keys: string[] = [];
        const regexStr =
            '^' +
            path.replace(/\/+$/, '').replace(/:[^/]+/g, (m) => {
                keys.push(m.slice(1));
                return '([^/]+)';
            }) +
            '$';

        return { keys, regex: new RegExp(regexStr) };
    }
}
